import { forwardRef } from 'react';
import type { Ref, RefObject } from 'react';

import TextArea from './TextArea';
import { useAutoResize } from './useAutoResize';
import type { TextAreaProps } from './TextArea';

export type FullContentTextAreaProps = TextAreaProps & {
  /**
   * Whether the textarea should automatically resize to fit content.
   * @default true
   */
  autoResize?: boolean;
};

/**
 * A textarea component that automatically resizes to show all content without overflow.
 * Extends the base TextArea component with auto-resizing functionality.
 */
const FullContentTextArea = ({
  autoResize = true,
  value,
  onChange,
  ...props
}: FullContentTextAreaProps, forwardedRef: Ref<HTMLTextAreaElement>) => {
  const { textareaRef: internalRef, handleChange } = useAutoResize({
    autoResize,
    value,
    onChange,
  });

  const textareaRef = (forwardedRef as RefObject<HTMLTextAreaElement>) || internalRef;

  return (
    <TextArea
      ref={textareaRef}
      value={value}
      onChange={handleChange}
      isResizable={autoResize ? false : props.isResizable}
      {...props}
    />
  );
};

export default forwardRef<HTMLTextAreaElement, FullContentTextAreaProps>(FullContentTextArea);
